/**
 * As 3 buscas sugeridas depois do onboarding ("Sua primeira busca").
 *
 * Ordem de escolha (proposta "Onboarding"): o segmento, quando tem lista própria; senão o setor;
 * senão o tipo de cliente. Quem vende para pessoas físicas recebe as buscas de parceiros do
 * bairro, porque as listas de empresas não fazem sentido para esse público.
 *
 * Cada busca diz o módulo (negócios locais, telefones ou emails), o termo, por que vale a pena e,
 * para telefones e emails, a rede. O lugar vem do IP (place.ts) e só pré-preenche o formulário.
 * Os textos em português ficam aqui (chaves `ob_sug_<id>_term|kw|why`).
 */
import type { CustomerId, ReachId } from './catalog';
import { cityLabel, dddTitle, type UserPlace } from './place';

export type SuggestionKind = 'loc' | 'tel' | 'mail';
export type Network = 'instagram' | 'linkedin';

export interface SuggestionDef {
  kind: SuggestionKind;
  network?: Network;
  /** Como aparece na janela ("Restaurantes"). */
  term: string;
  /** O que vai para o campo de busca ("restaurante"). */
  kw: string;
  why: string;
}

export const SUGGESTIONS: Record<string, SuggestionDef> = {
  // Marketing (proposta)
  mkt_restaurants: {
    kind: 'loc',
    term: 'Restaurantes',
    kw: 'restaurante',
    why: 'Dependem de movimento e costumam contratar agência para redes sociais e anúncios.',
  },
  mkt_aesthetics: {
    kind: 'loc',
    term: 'Clínicas de estética',
    kw: 'clínica de estética',
    why: 'Investem em marketing o ano todo para manter a agenda cheia.',
  },
  mkt_clothing: {
    kind: 'tel',
    network: 'instagram',
    term: 'Lojas de roupa',
    kw: 'loja de roupas',
    why: 'Vendem pelo Instagram e precisam de conteúdo e tráfego.',
  },
  // Clínica odontológica (proposta)
  dental_industries: {
    kind: 'loc',
    term: 'Indústrias',
    kw: 'indústria',
    why: 'Contratam convênio odontológico para os funcionários.',
  },
  dental_hr: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Gerentes de RH',
    kw: 'gerente de rh',
    why: 'Quem decide os benefícios da equipe costuma estar no LinkedIn.',
  },
  dental_accounting: {
    kind: 'loc',
    term: 'Escritórios de contabilidade',
    kw: 'escritório de contabilidade',
    why: 'Atendem muitas empresas e podem indicar o seu convênio.',
  },
  // Tipo de cliente (proposta)
  biz_shops: {
    kind: 'loc',
    term: 'Comércios e escritórios',
    kw: 'empresa',
    why: 'No Google Maps cada empresa aparece com telefone, endereço e site.',
  },
  biz_industries: {
    kind: 'loc',
    term: 'Indústrias',
    kw: 'indústria',
    why: 'Compram de fornecedores fixos e fecham contratos maiores.',
  },
  biz_owners: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Donos de empresa',
    kw: 'sócio proprietário',
    why: 'Quem decide a compra costuma ter perfil no LinkedIn.',
  },
  pro_lawyers: {
    kind: 'tel',
    network: 'instagram',
    term: 'Advogados',
    kw: 'advogado',
    why: 'Muitos deixam o WhatsApp do escritório à vista no perfil.',
  },
  pro_realtors: {
    kind: 'tel',
    network: 'instagram',
    term: 'Corretores de imóveis',
    kw: 'corretor de imóveis',
    why: 'Vivem de contato direto e respondem rápido.',
  },
  pro_doctors: {
    kind: 'mail',
    network: 'instagram',
    term: 'Médicos',
    kw: 'médico',
    why: 'Costumam pôr o email do consultório na biografia.',
  },
  ppl_gyms: {
    kind: 'loc',
    term: 'Academias',
    kw: 'academia',
    why: 'Reúnem o mesmo público que o seu e topam parcerias.',
  },
  ppl_salons: {
    kind: 'loc',
    term: 'Salões de beleza',
    kw: 'salão de beleza',
    why: 'Têm clientela fiel no bairro e indicam serviços.',
  },
  ppl_creators: {
    kind: 'tel',
    network: 'instagram',
    term: 'Criadores de conteúdo locais',
    kw: 'influenciador',
    why: 'Falam direto com o público que você quer alcançar.',
  },
  // Setores
  agro_farms: {
    kind: 'loc',
    term: 'Fazendas',
    kw: 'fazenda',
    why: 'Compram insumos, máquinas e serviços o ano todo.',
  },
  agro_stores: {
    kind: 'loc',
    term: 'Lojas agropecuárias',
    kw: 'loja agropecuária',
    why: 'Revendem para os produtores da região e procuram novos fornecedores.',
  },
  agro_producers: {
    kind: 'tel',
    network: 'instagram',
    term: 'Produtores rurais',
    kw: 'produtor rural',
    why: 'Muitos mostram o WhatsApp no perfil e negociam direto.',
  },
  food_restaurants: {
    kind: 'loc',
    term: 'Restaurantes',
    kw: 'restaurante',
    why: 'Compram alimentos e bebidas toda semana e trocam de fornecedor com frequência.',
  },
  food_markets: {
    kind: 'loc',
    term: 'Mercados e empórios',
    kw: 'mercado',
    why: 'Procuram produtos novos para a prateleira.',
  },
  food_events: {
    kind: 'tel',
    network: 'instagram',
    term: 'Organizadores de eventos',
    kw: 'organizador de eventos',
    why: 'Contratam comida e bebida para cada evento.',
  },
  cons_realestate: {
    kind: 'loc',
    term: 'Imobiliárias',
    kw: 'imobiliária',
    why: 'Indicam obras e reformas para quem compra ou aluga imóveis.',
  },
  cons_materials: {
    kind: 'loc',
    term: 'Lojas de materiais de construção',
    kw: 'materiais de construção',
    why: 'Atendem quem está em obra e indicam profissionais.',
  },
  cons_architects: {
    kind: 'tel',
    network: 'instagram',
    term: 'Arquitetos',
    kw: 'arquiteto',
    why: 'Definem a obra e indicam construtoras de confiança.',
  },
  com_buyers: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Compradores',
    kw: 'comprador',
    why: 'Quem cuida das compras das empresas costuma estar no LinkedIn.',
  },
  com_offices: {
    kind: 'loc',
    term: 'Escritórios',
    kw: 'escritório',
    why: 'Compram material, brindes e equipamentos com frequência.',
  },
  com_retailers: {
    kind: 'tel',
    network: 'instagram',
    term: 'Lojistas',
    kw: 'lojista',
    why: 'Procuram fornecedores novos pelo Instagram.',
  },
  edu_schools: {
    kind: 'loc',
    term: 'Escolas',
    kw: 'escola',
    why: 'Fecham parcerias e contratam cursos para alunos e professores.',
  },
  edu_hr: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Gerentes de RH',
    kw: 'gerente de rh',
    why: 'Contratam treinamentos para as equipes.',
  },
  edu_industries: {
    kind: 'loc',
    term: 'Indústrias',
    kw: 'indústria',
    why: 'Investem todo ano na formação das equipes.',
  },
  ener_industries: {
    kind: 'loc',
    term: 'Indústrias',
    kw: 'indústria',
    why: 'Pagam conta de energia alta e querem reduzir o custo.',
  },
  ener_supermarkets: {
    kind: 'loc',
    term: 'Supermercados',
    kw: 'supermercado',
    why: 'Ficam abertos o dia todo e sentem cada aumento na conta de luz.',
  },
  ener_producers: {
    kind: 'tel',
    network: 'instagram',
    term: 'Produtores rurais',
    kw: 'produtor rural',
    why: 'Gastam energia com irrigação e armazenagem e procuram alternativas.',
  },
  fab_buyers: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Gerentes de compras',
    kw: 'gerente de compras',
    why: 'Quem escolhe os fornecedores costuma estar no LinkedIn.',
  },
  fab_distributors: {
    kind: 'loc',
    term: 'Distribuidoras',
    kw: 'distribuidora',
    why: 'Revendem para muitos clientes e buscam novas linhas de produto.',
  },
  fab_wholesale: {
    kind: 'loc',
    term: 'Atacados',
    kw: 'atacado',
    why: 'Compram em volume e com frequência.',
  },
  log_industries: {
    kind: 'loc',
    term: 'Indústrias',
    kw: 'indústria',
    why: 'Despacham mercadoria toda semana e precisam de frete confiável.',
  },
  log_distributors: {
    kind: 'loc',
    term: 'Distribuidoras',
    kw: 'distribuidora',
    why: 'Entregam para muitos clientes e terceirizam o transporte.',
  },
  log_ecommerce: {
    kind: 'tel',
    network: 'instagram',
    term: 'Lojas virtuais',
    kw: 'loja virtual',
    why: 'Enviam pedidos todo dia e comparam preço de frete.',
  },
  imo_builders: {
    kind: 'loc',
    term: 'Construtoras',
    kw: 'construtora',
    why: 'Lançam imóveis e procuram parceiros para vender.',
  },
  imo_owners: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Donos de empresa',
    kw: 'sócio proprietário',
    why: 'Procuram salas, galpões e imóveis para investir.',
  },
  imo_accounting: {
    kind: 'loc',
    term: 'Escritórios de contabilidade',
    kw: 'escritório de contabilidade',
    why: 'Atendem empresas que abrem, mudam ou ampliam o endereço.',
  },
  rep_stores: {
    kind: 'loc',
    term: 'Lojas e revendas',
    kw: 'loja',
    why: 'São elas que compram os produtos que você representa.',
  },
  rep_supermarkets: {
    kind: 'loc',
    term: 'Supermercados',
    kw: 'supermercado',
    why: 'Compram de vários fornecedores e recebem representantes.',
  },
  rep_retailers: {
    kind: 'tel',
    network: 'instagram',
    term: 'Lojistas',
    kw: 'lojista',
    why: 'Muitos negociam pelo WhatsApp que deixam no perfil.',
  },
  sau_industries: {
    kind: 'loc',
    term: 'Indústrias',
    kw: 'indústria',
    why: 'Contratam convênios e programas de saúde para os funcionários.',
  },
  sau_hr: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Gerentes de RH',
    kw: 'gerente de rh',
    why: 'Quem decide os benefícios da equipe costuma estar no LinkedIn.',
  },
  sau_gyms: {
    kind: 'loc',
    term: 'Academias',
    kw: 'academia',
    why: 'Atendem o mesmo público e topam parcerias e indicações.',
  },
  seg_accounting: {
    kind: 'loc',
    term: 'Escritórios de contabilidade',
    kw: 'escritório de contabilidade',
    why: 'Atendem muitas empresas e podem indicar seguros e planos.',
  },
  seg_owners: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Donos de empresa',
    kw: 'sócio proprietário',
    why: 'Decidem o plano de saúde e os seguros da empresa.',
  },
  seg_carriers: {
    kind: 'loc',
    term: 'Transportadoras',
    kw: 'transportadora',
    why: 'Precisam de seguro de carga e de frota.',
  },
  fin_cars: {
    kind: 'loc',
    term: 'Lojas de veículos',
    kw: 'loja de veículos',
    why: 'Precisam de crédito e financiamento para fechar vendas.',
  },
  fin_owners: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Donos de empresa',
    kw: 'sócio proprietário',
    why: 'Buscam capital de giro e crédito para crescer.',
  },
  fin_realestate: {
    kind: 'loc',
    term: 'Imobiliárias',
    kw: 'imobiliária',
    why: 'Indicam financiamento e consórcio para quem compra imóvel.',
  },
  srv_owners: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Donos de empresa',
    kw: 'sócio proprietário',
    why: 'Quem contrata serviços para a empresa costuma estar no LinkedIn.',
  },
  srv_clinics: {
    kind: 'loc',
    term: 'Clínicas',
    kw: 'clínica',
    why: 'Têm a rotina cheia e terceirizam o que não é atendimento.',
  },
  srv_stores: {
    kind: 'loc',
    term: 'Lojas',
    kw: 'loja',
    why: 'Precisam de contabilidade, jurídico e apoio no dia a dia.',
  },
  tec_it: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Diretores de TI',
    kw: 'diretor de ti',
    why: 'Decidem sistemas e fornecedores de tecnologia.',
  },
  tec_clinics: {
    kind: 'loc',
    term: 'Clínicas',
    kw: 'clínica',
    why: 'Usam sistemas de agenda e prontuário e querem automatizar.',
  },
  tec_accounting: {
    kind: 'loc',
    term: 'Escritórios de contabilidade',
    kw: 'escritório de contabilidade',
    why: 'Atendem muitas empresas e dependem de sistemas.',
  },
  tel_condos: {
    kind: 'loc',
    term: 'Condomínios',
    kw: 'condomínio',
    why: 'Fecham contrato de internet para o prédio inteiro.',
  },
  tel_offices: {
    kind: 'loc',
    term: 'Escritórios',
    kw: 'escritório',
    why: 'Precisam de internet e telefone que não caem.',
  },
  tel_hotels: {
    kind: 'loc',
    term: 'Hotéis e pousadas',
    kw: 'pousada',
    why: 'Oferecem Wi-Fi aos hóspedes e trocam de provedor quando ele falha.',
  },
  tur_hr: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Gerentes de RH',
    kw: 'gerente de rh',
    why: 'Organizam viagens, confraternizações e eventos da equipe.',
  },
  tur_schools: {
    kind: 'loc',
    term: 'Escolas',
    kw: 'escola',
    why: 'Fazem passeios e formaturas todo ano.',
  },
  tur_agencies: {
    kind: 'loc',
    term: 'Agências de viagens',
    kw: 'agência de viagens',
    why: 'Revendem pacotes e procuram parceiros locais.',
  },
  gov_churches: {
    kind: 'loc',
    term: 'Igrejas',
    kw: 'igreja',
    why: 'Reúnem a comunidade e fazem parcerias com instituições.',
  },
  gov_csr: {
    kind: 'mail',
    network: 'linkedin',
    term: 'Gestores de responsabilidade social',
    kw: 'responsabilidade social',
    why: 'Destinam verba das empresas para projetos sociais.',
  },
  gov_schools: {
    kind: 'loc',
    term: 'Escolas',
    kw: 'escola',
    why: 'Fecham parcerias com projetos sociais e culturais.',
  },
};

export type SuggestionId = keyof typeof SUGGESTIONS;

const BY_SEGMENT: Record<string, SuggestionId[]> = {
  clinica_odontologica: ['dental_industries', 'dental_hr', 'dental_accounting'],
};

const BY_SECTOR: Record<string, SuggestionId[]> = {
  mkt: ['mkt_restaurants', 'mkt_aesthetics', 'mkt_clothing'],
  agro: ['agro_farms', 'agro_stores', 'agro_producers'],
  alim: ['food_restaurants', 'food_markets', 'food_events'],
  cons: ['cons_realestate', 'cons_materials', 'cons_architects'],
  com: ['com_buyers', 'com_offices', 'com_retailers'],
  edu: ['edu_schools', 'edu_hr', 'edu_industries'],
  ener: ['ener_industries', 'ener_supermarkets', 'ener_producers'],
  fab: ['fab_buyers', 'fab_distributors', 'fab_wholesale'],
  log: ['log_industries', 'log_distributors', 'log_ecommerce'],
  imo: ['imo_builders', 'imo_owners', 'imo_accounting'],
  rep: ['rep_stores', 'rep_supermarkets', 'rep_retailers'],
  sau: ['sau_industries', 'sau_hr', 'sau_gyms'],
  seg: ['seg_accounting', 'seg_owners', 'seg_carriers'],
  fin: ['fin_cars', 'fin_owners', 'fin_realestate'],
  srv: ['srv_owners', 'srv_clinics', 'srv_stores'],
  tec: ['tec_it', 'tec_clinics', 'tec_accounting'],
  tel: ['tel_condos', 'tel_offices', 'tel_hotels'],
  tur: ['tur_hr', 'tur_schools', 'tur_agencies'],
  gov: ['gov_churches', 'gov_csr', 'gov_schools'],
};

const BY_CUSTOMER: Record<CustomerId, SuggestionId[]> = {
  empresas: ['biz_shops', 'biz_industries', 'biz_owners'],
  todos: ['biz_shops', 'biz_industries', 'biz_owners'],
  profissionais: ['pro_lawyers', 'pro_realtors', 'pro_doctors'],
  pessoas: ['ppl_gyms', 'ppl_salons', 'ppl_creators'],
};

export function pickSuggestions(input: {
  sectorId?: string | null;
  segmentId?: string | null;
  customer?: CustomerId | null;
}): SuggestionId[] {
  if (input.customer === 'pessoas') return BY_CUSTOMER.pessoas;
  if (input.segmentId && BY_SEGMENT[input.segmentId]) return BY_SEGMENT[input.segmentId] ?? [];
  if (input.sectorId && BY_SECTOR[input.sectorId]) return BY_SECTOR[input.sectorId] ?? [];
  return BY_CUSTOMER[input.customer ?? 'empresas'];
}

/** Onde a busca acontece, já com o lugar da pessoa (textos vêm de quem chama). */
export interface PlaceWords {
  /** "em Curitiba/PR" */
  inCity: (city: string) => string;
  /** "em Curitiba/PR e região" */
  inRegion: (city: string) => string;
  /** "no Paraná" */
  inState: (place: UserPlace) => string;
  /** "em todo o Brasil" / "em todo o país" */
  inCountry: (place: UserPlace) => string;
  /** "Curitiba/PR e região" (sem preposição) */
  region: (city: string) => string;
  /** "todo o Brasil" / "todo o país" */
  country: (place: UserPlace) => string;
  allAreaCodes: string;
  areaCode: (ddd: string) => string;
}

/** Pedaço do formulário de captura que a sugestão pré-preenche. */
export interface SuggestionForm {
  module: 'places' | 'phones' | 'emails';
  keywords: string;
  sites?: string[];
  domains?: string[];
  /** Cidade para pôr no campo de locais (places). */
  city?: string;
  distance?: number;
  country?: string;
  areaCode?: { value: string; text: string };
}

const NETWORK_NAME: Record<Network, string> = { instagram: 'Instagram', linkedin: 'LinkedIn' };
const NETWORK_SITE: Record<Network, string> = {
  instagram: 'instagram.com',
  linkedin: 'linkedin.com',
};
const FREE_EMAILS = [
  '@gmail.com|Gmail',
  '@hotmail.com|Hotmail',
  '@outlook.com|Outlook',
  '@yahoo.com|Yahoo',
];

const quoted = (kw: string): string => (kw.includes(' ') ? `"${kw}"` : kw);

/** Lugar mostrado e o formulário de uma sugestão. */
export function suggestionWhere(
  def: SuggestionDef,
  kw: string,
  reach: ReachId,
  place: UserPlace,
  words: PlaceWords,
): { where: string; form: SuggestionForm } {
  const city = cityLabel(place);
  const local = reach === 'cidade' || reach === 'regiao';
  const state = reach === 'estado' && place.state ? place.state : undefined;

  if (def.kind === 'loc') {
    let where = '';
    if (reach === 'cidade' && city) where = words.inCity(city);
    else if (reach === 'regiao' && city) where = words.inRegion(city);
    else if (reach === 'estado' && (place.state || place.region)) where = words.inState(place);
    else if (!local && reach !== 'estado') where = words.inCountry(place);
    else if (place.state || place.region) where = words.inState(place);
    // Os negócios locais sempre começam pela cidade da pessoa (dá para somar outras na busca).
    return {
      where,
      form: {
        module: 'places',
        keywords: kw,
        city: place.city ? city : undefined,
        distance: reach === 'cidade' ? undefined : 16,
      },
    };
  }

  const network = def.network ?? 'instagram';
  const brazil = place.country === 'BR';

  if (def.kind === 'tel') {
    const ddd = local && brazil ? place.ddd : undefined;
    // Sem DDD certo na cidade, a cidade vai junto da palavra de busca.
    const cityInKeywords = local && !ddd && Boolean(place.city);
    const area = ddd ? words.areaCode(ddd) : cityInKeywords ? city : words.allAreaCodes;
    return {
      where: `${NETWORK_NAME[network]} · ${area}`,
      form: {
        module: 'phones',
        keywords: cityInKeywords ? `${quoted(kw)} ${place.city}` : quoted(kw),
        sites: [NETWORK_SITE[network]],
        country: place.country ? place.country.toLowerCase() : undefined,
        areaCode: ddd ? { value: ddd, text: dddTitle(ddd) } : undefined,
      },
    };
  }

  // Sem lugar conhecido para cidade/região/estado, mostra só a rede (o lugar fica para a busca).
  let area = local || reach === 'estado' ? '' : words.country(place);
  let placeWord = '';
  if (local && place.city) {
    area = reach === 'regiao' ? words.region(city) : city;
    placeWord = place.city;
  } else if ((local || reach === 'estado') && (state || place.region)) {
    area = state ? state.name : (place.region ?? '');
    placeWord = area;
  }
  return {
    where: area ? `${NETWORK_NAME[network]} · ${area}` : NETWORK_NAME[network],
    form: {
      module: 'emails',
      keywords: placeWord ? `${quoted(kw)} ${placeWord}` : quoted(kw),
      sites: [NETWORK_SITE[network]],
      domains: FREE_EMAILS,
    },
  };
}
